import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Bus } from '../model/bus';
import { AssignmentType } from '../model/assignmentType';
import { UserMapping } from '../model/usermapping';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ScheduleService } from '../services/schedule.service';
import { BusService } from '../services/bus.service';
import { UserMappingService } from '../services/usermapping.service';
import { List } from 'immutable';
import { DateTimeUtils } from '../shared/datetimeutils';
import { Schedule } from '../model/schedule';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmDialog } from '../confirmation/confirm.dialog';
import { DialogResult } from './work-schedule.component';
import { WorkScheduleValidator } from './work-schedule-validator';
import { BusWithOverlaps } from '../model/busWithOverlap';

@Component({
  selector: 'app-edit-work-schedule',
  templateUrl: './edit-work-schedule.component.html',
  styleUrls: ['./work-schedule.component.css']
})
export class EditWorkScheduleComponent implements OnInit {
  public form: UntypedFormGroup;
  public driver: string = "";
  public type: string = "";
  public bus: string = "";
  public from: string = "";
  public to: string = "";
  public note: string = "";
  public activityName: string = "";
  public buses: Bus[];
  public assignmentTypes: AssignmentType[];
  public timeListFrom: string[];
  public timeListTo: string[];
  public userMappings: UserMapping[];
  public bussesFormControl = new UntypedFormControl();
  public typesFormControl = new UntypedFormControl();
  public driversFormControl = new UntypedFormControl({ value: null, disabled: true });
  public fromFormControl = new UntypedFormControl();
  public toFormControl = new UntypedFormControl();

  private readonly schedule: Schedule;
  private readonly activeDate: string;
  private confirmModalRef: MatDialogRef<ConfirmDialog>;
  private readonly noBusSelected: string = "Ingen buss";

  constructor(public dialogRef: MatDialogRef<EditWorkScheduleComponent>,
    @Inject(MAT_DIALOG_DATA) private readonly data: any,
    private readonly fb: UntypedFormBuilder,
    private readonly scheduleService: ScheduleService,
    private readonly busService: BusService,
    private readonly userMappingService: UserMappingService,
    private readonly toastr: ToastrService,
    private readonly dialog: MatDialog) {
    this.timeListFrom = DateTimeUtils.getTimeArrayQuartersFrom();
    this.timeListTo = DateTimeUtils.getTimeArrayQuartersTo();

    this.typesFormControl = new UntypedFormControl(Validators.required);
    this.bussesFormControl = new UntypedFormControl();
    this.driversFormControl = new UntypedFormControl({ value: null, disabled: true }, Validators.required);

    this.schedule = data.schedule;
    this.activeDate = data.activeDate;
    this.setSelectedDates();

  }

  ngOnInit(): void {
    this.getAvailableBuses();
    this.scheduleService.getAssignmentTypes().then((assignmentTypes: AssignmentType[]) => {
      this.assignmentTypes = assignmentTypes;
      let rx = /[a-zA-Z]+/;
      let skill = rx.exec(this.schedule.skill);
      const selectedAssignmentType = this.assignmentTypes.find((at: AssignmentType) => at.name === skill[0])
      this.typesFormControl.setValue(selectedAssignmentType);
    });

    this.userMappingService.UserMappings.subscribe((userMappings: List<UserMapping>) => {
      this.userMappings = userMappings.sort(
        (a, b) => a.name.localeCompare(b.name)).toArray();
      const selectedUserMapping = this.userMappings.find((um: UserMapping) => um.id === this.schedule.userMappingId);
      this.driversFormControl.setValue(selectedUserMapping);
    });

    this.form = this.fb.group(
      {
        driver: this.driversFormControl,
        type: this.typesFormControl,
        bussesFc: this.bussesFormControl,
        from: this.fromFormControl,
        to: this.toFormControl,
        note: new UntypedFormControl(this.schedule.comment),
        activityName: new UntypedFormControl(this.schedule.activityName),
        activeDate: this.activeDate
      },
      { validator: WorkScheduleValidator.timeRangeValidator });


    this.typesFormControl.valueChanges.subscribe((value: any) => {
      if (value.name.toLocaleUpperCase() === 'KOOR' || value.name.toLocaleUpperCase() === 'OTHER'){
        this.bussesFormControl.clearValidators();
        this.bussesFormControl.reset();        
      }      
      this.getAvailableBuses();
    });
  }

  public submit() {
    const schedule = new Schedule();
    schedule.buss = this.bussesFormControl.value?.number == this.noBusSelected ? null : (this.bussesFormControl.value?.number ?? "Okänd");
    schedule.comment = this.form.controls['note'].value;
    schedule.name = this.driversFormControl.value?.name;
    schedule.userMappingId = this.driversFormControl.value.id;
    schedule.skill = "[\"" + this.typesFormControl.value.name + "\"]";
    schedule.activityName = this.form.controls['activityName'].value;
    schedule.airportId = this.schedule.airportId;

    //start
    let f = this.fromFormControl.value as string;
    schedule.start = DateTimeUtils.convertStringToDateFrom(this.activeDate, f);

    //end
    f = this.toFormControl.value as string;
    schedule.end = DateTimeUtils.convertStringToDateTo(this.activeDate, f);

    schedule.id = this.schedule.id;
    this.scheduleService.updateSchedule(schedule)
      .then((updatedSchedule: Schedule) => {
        const result = new DialogResult();
        result.deleted = false;
        result.schedule = Schedule.ToTypeSafe(updatedSchedule);
        result.schedule.checkedin = this.schedule.checkedin;
        this.dialogRef.close(result);
        this.toastr.success("Schema uppdaterat för " + schedule.name);
      })
      .catch((err: HttpErrorResponse) => {
        this.toastr.error(err.error, "Error");
      });
  }

  public canSave() {
    return this.form.dirty && !this.form.invalid;
  }

  public deleteSchema() {

    this.confirmModalRef = this.dialog.open(ConfirmDialog, {
      disableClose: false,
      width: "400px",
      autoFocus: false,
      closeOnNavigation: false, data:
      {
        id: 1,
        title: "Bekräfta",
        text: "Vill du verkligen radera  schemat för " + this.driversFormControl.value.name + "?<br>"
      }
    });

    this.confirmModalRef.afterClosed().subscribe(result => {
      if (result) {
        this.scheduleService.deleteSchedule(this.schedule)
          .then(() => {
            this.toastr.success("Schemarad raderat");
            const result = new DialogResult();
            result.deleted = true;
            result.schedule = this.schedule;
            this.dialogRef.close(result);

          })
          .catch(() => this.toastr.error("Det gick inte att radera schemat."))
      }
    });

  }

  public toChanged(event) {
    this.getAvailableBuses();
  }

  private getFromValue(): Date {
    let f = this.fromFormControl.value as string;
    return DateTimeUtils.convertStringToDateFrom(this.activeDate, f);
  }

  private getToValue(): Date {
    let f = this.toFormControl.value as string;
    let date = DateTimeUtils.convertStringToDateTo(this.activeDate, f);

    return date;
  }

  private getSelectableTime(date: Date): string {
    let hourString = "";
    let minuteString = "";
    if (date.getHours() < 10) {
      hourString = "0" + date.getHours();
    }
    else {
      hourString = date.getHours().toString();
    }

    if (date.getMinutes() < 10) {
      minuteString = "0" + date.getMinutes();
    }
    else {
      minuteString = date.getMinutes().toString();
    }

    return hourString + ':' + minuteString;
  }

  private setSelectedDates() {
    const start = new Date(this.schedule.start);
    let startString = this.getSelectableTime(start);


    const end = new Date(this.schedule.end);
    let endString = this.getSelectableTime(end);

    this.fromFormControl = new UntypedFormControl(startString, Validators.required);
    this.toFormControl = new UntypedFormControl(endString, Validators.required);

  }

  private getAvailableBuses(): void {
    this.busService.getAvailableBuses(this.schedule.id, this.getFromValue(), this.getToValue()).then((buses: BusWithOverlaps[]) => {
      this.buses = buses;

      let bus = new BusWithOverlaps();
      bus.number = this.noBusSelected;
      this.buses.unshift(bus);

      this.buses.sort((b1: BusWithOverlaps, b2: BusWithOverlaps) => +b1.number - +b2.number);
      if (this.typesFormControl.value.name.toLocaleUpperCase() == 'KOOR' || this.typesFormControl.value.name.toLocaleUpperCase() == 'OTHER') {
        this.bussesFormControl.setValue('');
      }
      else {
        const selectedBus = this.buses.find((bus: BusWithOverlaps) => bus.number === this.schedule.buss);
        this.bussesFormControl.setValue(selectedBus);
      }

      this.bussesFormControl.updateValueAndValidity();
    });
  }
}
